import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  Stack
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { Save, CloudUpload, Delete } from "@material-ui/icons";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentIcon from "@mui/icons-material/Payment";
import EmailIcon from "@mui/icons-material/Email";
import TimerIcon from "@mui/icons-material/Timer";
import ImageIcon from "@mui/icons-material/Image";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http")) return value;

  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return value;

  const normalizedBase = base.replace(/\/+$/, "");
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${normalizedBase}${path}`;
};

const GlobalConfig = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({
    loginLogo: false,
    loginBackground: false
  });

  const [config, setConfig] = useState({
    mpAccessToken: "",
    smtpHost: "",
    smtpPort: "",
    smtpSecure: "false",
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
    trialExpiration: "",
    loginLogo: "",
    loginBackground: "",
    loginWhatsapp: ""
  });

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === "trialExpiration") {
      const onlyDigits = value.replace(/\D/g, "");
      return setConfig(prev => ({ ...prev, [name]: onlyDigits }));
    }

    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.put("/global-config", config);
      toast.success("Configurações salvas com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/global-config");
      setConfig(prev => ({
        ...prev,
        ...data,
        trialExpiration:
          data.trialExpiration !== undefined && data.trialExpiration !== null
            ? String(data.trialExpiration)
            : prev.trialExpiration
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleBrandingUpload = async (field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("field", field);

    try {
      setUploading(prev => ({ ...prev, [field]: true }));
      const { data } = await api.post("/global-config/upload", formData);

      const url = data?.url || data?.[field];

      if (url) {
        setConfig(prev => ({
          ...prev,
          [field]: url
        }));
      }

      toast.success("Imagem atualizada com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleRemoveImage = async (field) => {
    try {
      setConfig(prev => ({
        ...prev,
        [field]: ""
      }));
      toast.success("Imagem removida com sucesso.");
    } catch (err) {
      toastError(err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              bgcolor: PRIMARY_MAIN,
              "& svg": { width: 28, height: 28, color: "#fff" }
            }}
          >
            <SettingsIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                mb: 0.5,
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.5rem", md: "1.875rem" }
              }}
            >
              Configurações Globais
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
              Configure os parâmetros gerais da plataforma
            </Typography>
          </Box>
        </Stack>

        {/* Tabs Container */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Tabs Header */}
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
            <Tabs
              value={tab}
              onChange={(e, newValue) => setTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  minHeight: 56,
                  color: "text.secondary",
                  "&.Mui-selected": {
                    color: PRIMARY_MAIN
                  }
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: PRIMARY_MAIN,
                  height: 3
                }
              }}
            >
              <Tab icon={<PaymentIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Mercado Pago" />
              <Tab icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="E-mail (SMTP)" />
              <Tab icon={<TimerIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Trial / Assinatura" />
              <Tab icon={<ImageIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Login / Capa" />
            </Tabs>
          </Box>

          {/* Tabs Content */}
          <Box
            sx={{
              p: 4,
              maxHeight: "calc(100vh - 280px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
              "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "4px" },
              "&::-webkit-scrollbar-thumb:hover": { background: "#555" }
            }}
          >
            {/* Tab 0: Mercado Pago */}
            {tab === 0 && (
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, fontSize: "1.125rem" }}>
                  Mercado Pago
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Access Token
                    </Typography>
                    <TextField
                      name="mpAccessToken"
                      value={config.mpAccessToken}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Digite o Access Token do Mercado Pago"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                      Use o Access Token do Mercado Pago da conta principal da plataforma. As empresas clientes usarão sempre essa configuração.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tab 1: SMTP */}
            {tab === 1 && (
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, fontSize: "1.125rem" }}>
                  E-mail (SMTP)
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Host
                    </Typography>
                    <TextField
                      name="smtpHost"
                      value={config.smtpHost}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="smtp.gmail.com"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Porta
                    </Typography>
                    <TextField
                      name="smtpPort"
                      value={config.smtpPort}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="587"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Secure
                    </Typography>
                    <TextField
                      name="smtpSecure"
                      value={config.smtpSecure}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="false"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
                      "true" para SSL/TLS, "false" para STARTTLS
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      E-mail
                    </Typography>
                    <TextField
                      name="smtpUser"
                      value={config.smtpUser}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="seu-email@gmail.com"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Senha de app
                    </Typography>
                    <TextField
                      name="smtpPass"
                      value={config.smtpPass}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="password"
                      placeholder="••••••••••••••••"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Remetente (FROM)
                    </Typography>
                    <TextField
                      name="smtpFrom"
                      value={config.smtpFrom}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder='Sua Empresa <suporte@gmail.com>'
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                      Exemplo: Sua empresa &lt;suporte@gmail.com&gt;
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tab 2: Trial */}
            {tab === 2 && (
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, fontSize: "1.125rem" }}>
                  Trial / Período de Teste
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Dias de teste
                    </Typography>
                    <TextField
                      name="trialExpiration"
                      value={config.trialExpiration}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 1 }}
                      placeholder="3"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                      Quantidade de dias de teste que a empresa nova terá. Se vazio, o sistema usa o valor padrão do .env (APP_TRIALEXPIRATION, ex.: 3).
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tab 3: Login/Capa */}
            {tab === 3 && (
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, fontSize: "1.125rem" }}>
                  Login / Capa
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Logo do login
                    </Typography>

                    {config.loginLogo && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f9f9f9", borderRadius: 2, border: "1px solid", borderColor: "divider", display: "inline-block" }}>
                        <img
                          src={resolveImageUrl(config.loginLogo)}
                          alt="Logo do login"
                          style={{ maxWidth: "200px", maxHeight: "80px", borderRadius: "8px", objectFit: "contain" }}
                        />
                      </Box>
                    )}

                    <input
                      id="loginLogoUpload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleBrandingUpload("loginLogo", e.target.files[0])}
                    />
                    <Stack direction="row" spacing={1}>
                      <label htmlFor="loginLogoUpload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUpload />}
                          disabled={uploading.loginLogo}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.875rem",
                            borderRadius: 2
                          }}
                        >
                          {uploading.loginLogo ? "Enviando..." : "Enviar logo"}
                        </Button>
                      </label>
                      {config.loginLogo && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleRemoveImage("loginLogo")}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.875rem",
                            borderRadius: 2
                          }}
                        >
                          Remover
                        </Button>
                      )}
                    </Stack>

                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                      Se nenhuma imagem for enviada, o sistema usa /logo.png.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Imagem de fundo (capa)
                    </Typography>

                    {config.loginBackground && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f9f9f9", borderRadius: 2, border: "1px solid", borderColor: "divider", display: "inline-block" }}>
                        <img
                          src={resolveImageUrl(config.loginBackground)}
                          alt="Capa do login"
                          style={{ maxWidth: "200px", maxHeight: "80px", borderRadius: "8px", objectFit: "contain" }}
                        />
                      </Box>
                    )}

                    <input
                      id="loginBackgroundUpload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleBrandingUpload("loginBackground", e.target.files[0])}
                    />
                    <Stack direction="row" spacing={1}>
                      <label htmlFor="loginBackgroundUpload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUpload />}
                          disabled={uploading.loginBackground}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.875rem",
                            borderRadius: 2
                          }}
                        >
                          {uploading.loginBackground ? "Enviando..." : "Enviar capa"}
                        </Button>
                      </label>
                      {config.loginBackground && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleRemoveImage("loginBackground")}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.875rem",
                            borderRadius: 2
                          }}
                        >
                          Remover
                        </Button>
                      )}
                    </Stack>

                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                      Recomendada imagem em .webp ou .jpg. Se vazio, o sistema usa a capa padrão.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: "0.875rem" }}>
                      Link do WhatsApp do login
                    </Typography>
                    <TextField
                      name="loginWhatsapp"
                      value={config.loginWhatsapp}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="https://wa.me/5541999999999"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                          "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                      Exemplo: https://wa.me/5541999999999. Usado no botão de WhatsApp da tela de login e no botão "Chamar suporte".
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Botão Salvar */}
            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                startIcon={!saving && <Save />}
                disabled={saving}
                sx={{
                  bgcolor: PRIMARY_MAIN,
                  color: "#fff",
                  px: 4,
                  py: 1.2,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                  "&:hover": {
                    bgcolor: PRIMARY_DARK,
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)"
                  }
                }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : "Salvar Configurações"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default GlobalConfig;